import React, { useCallback, useEffect, useState } from "react";
import api, { USIA_FILTER_OPTIONS, hitungUsia } from "@/lib/api";
import Layout, { PageTitle, StatCard } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { SiswaDetailModal } from "@/pages/admin/SiswaDetailModal";
import { KETUNAAN_LIST } from "@/lib/constants";
import BackupExcelButton from "@/components/BackupExcelButton";
import { GraduationCap, Eye } from "lucide-react";

const F_EMPTY = { q: "", jenis_kelamin: "", status: "", ketunaan: "", usia: "" };

export function DataSiswaKepsek() {
  const [all, setAll] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: st }] = await Promise.all([api.get("/siswa"), api.get("/siswa/stats")]);
      setAll(data); setStats(st);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((s) => {
    if (applied.q && !s.nama.toLowerCase().includes(applied.q.toLowerCase())) return false;
    if (applied.jenis_kelamin && s.jenis_kelamin !== applied.jenis_kelamin) return false;
    if (applied.status && s.status !== applied.status) return false;
    if (applied.ketunaan && (s.ketunaan || "") !== applied.ketunaan) return false;
    if (applied.usia) {
      const u = s.usia ?? hitungUsia(s.tanggal_lahir);
      if (u == null) return false;
      if (applied.usia === "<5") { if (!(u < 5)) return false; }
      else if (u !== parseInt(applied.usia, 10)) return false;
    }
    return true;
  });

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Data Siswa" action={<BackupExcelButton endpoint="siswa" filters={applied} testId="backup-siswa-btn" />} />
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Siswa Aktif" value={stats.aktif ?? 0} icon={GraduationCap} />
        <StatCard label="Tidak Aktif" value={stats.tidak_aktif ?? 0} icon={GraduationCap} />
        <StatCard label="Perempuan Aktif" value={stats.perempuan_aktif ?? 0} icon={GraduationCap} />
        <StatCard label="Laki-laki Aktif" value={stats.laki_aktif ?? 0} icon={GraduationCap} />
      </section>
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="kepsek-siswa-filter"
        fields={[
          { key: "q", type: "text", label: "Nama", placeholder: "Cari nama siswa" },
          { key: "jenis_kelamin", type: "select", label: "Jenis Kelamin", options: [{ v: "L", label: "Laki-laki" }, { v: "P", label: "Perempuan" }] },
          { key: "status", type: "select", label: "Status", options: [{ v: "aktif", label: "Aktif" }, { v: "tidak_aktif", label: "Tidak Aktif" }] },
          { key: "ketunaan", type: "select", label: "Ketunaan", options: KETUNAAN_LIST },
          { key: "usia", type: "select", label: "Usia", options: USIA_FILTER_OPTIONS },
        ]}
      />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">NISN</th>
            <th className="text-left px-5 py-3">Nama</th>
            <th className="text-left px-5 py-3">JK</th>
            <th className="text-left px-5 py-3">Usia</th>
            <th className="text-left px-5 py-3">Ketunaan</th>
            <th className="text-left px-5 py-3">Alamat</th>
            <th className="text-left px-5 py-3">Status</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400 italic">Belum ada data.</td></tr>}
            {items.map((s) => {
              const u = s.usia ?? hitungUsia(s.tanggal_lahir);
              return (
              <tr key={s.id} className="pp-row">
                <td className="px-5 py-3 font-mono text-xs">{s.nis}</td>
                <td className="px-5 py-3 font-medium">{s.nama}</td>
                <td className="px-5 py-3">{s.jenis_kelamin === "L" ? "L" : "P"}</td>
                <td className="px-5 py-3 text-slate-700">{u == null ? "—" : `${u} th`}</td>
                <td className="px-5 py-3 text-xs text-slate-600">{s.ketunaan || "—"}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">{s.alamat || "—"}</td>
                <td className="px-5 py-3"><span className={s.status === "aktif" ? "pp-badge-success" : "pp-badge-danger"}>{s.status}</span></td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setDetail(s)} className="pp-btn-ghost text-xs" data-testid={`kepsek-siswa-detail-${s.id}`}><Eye className="w-3.5 h-3.5" />Detail</button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <SiswaDetailModal detail={detail} onClose={() => setDetail(null)} />
    </Layout>
  );
}
